/**
 * PortfolioSnapshot - Compact summary row for the Smart Queue Dashboard
 *
 * Shows: Total value, today's change, alert count, pending tasks count
 */
import { TrendBadge } from '../common'

interface PortfolioSnapshotProps {
  totalValue?: number | null
  totalCost?: number | null
  gainLoss?: number | null
  gainLossPct?: number | null
  alertCount: number
  pendingCount?: number
  lastUpdated?: string
  isLoading?: boolean
}

function formatCurrency(value: number | null | undefined): string {
  if (value === null || value === undefined) return '-'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value)
}

export function PortfolioSnapshot({
  totalValue,
  totalCost,
  gainLoss,
  gainLossPct,
  alertCount,
  pendingCount = 0,
  lastUpdated,
  isLoading,
}: PortfolioSnapshotProps) {
  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 animate-pulse">
        <div className="flex items-center justify-between">
          <div className="h-8 bg-gray-200 rounded w-32" />
          <div className="flex gap-4">
            <div className="h-6 bg-gray-200 rounded w-20" />
            <div className="h-6 bg-gray-200 rounded w-16" />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
      <div className="flex flex-wrap items-center justify-between gap-4">
        {/* Portfolio value */}
        <div className="flex items-baseline gap-3">
          <span className="text-2xl font-bold text-gray-900">
            {formatCurrency(totalValue)}
          </span>
          {gainLossPct != null && (
            <TrendBadge value={gainLossPct} format="percent" />
          )}
        </div>

        {/* Quick stats */}
        <div className="flex items-center gap-6 text-sm">
          {/* Cost basis */}
          <div className="hidden sm:block">
            <span className="text-gray-500">Cost:</span>{' '}
            <span className="font-medium text-gray-700">{formatCurrency(totalCost)}</span>
          </div>

          {/* Gain/Loss */}
          <div className="hidden md:block">
            <span className="text-gray-500">Gain/Loss:</span>{' '}
            <span className={`font-medium ${(gainLoss ?? 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {formatCurrency(gainLoss)}
            </span>
          </div>

          {/* Alert count */}
          {alertCount > 0 && (
            <div className="flex items-center gap-1.5">
              <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-medium bg-red-100 text-red-700 rounded-full">
                {alertCount}
              </span>
              <span className="text-gray-600">alert{alertCount !== 1 ? 's' : ''}</span>
            </div>
          )}

          {/* Pending tasks */}
          {pendingCount > 0 && (
            <div className="flex items-center gap-1.5">
              <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">
                {pendingCount}
              </span>
              <span className="text-gray-600">pending</span>
            </div>
          )}

          {/* Last updated */}
          {lastUpdated && (
            <span className="hidden lg:block text-gray-400">
              Updated {new Date(lastUpdated).toLocaleTimeString()}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}
